import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lightning, Play, Clock, Trophy, FloppyDisk, Stop, HandPalm,
  YoutubeLogo, CaretDown, CaretUp, VideoCamera
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LivePanel({ players, tournaments, matches, fetchData }) {
  const [selectedTournamentId, setSelectedTournamentId] = useState("");
  const [scheduledMatches, setScheduledMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [goingLiveId, setGoingLiveId] = useState(null);

  // Live scoring state
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [scoreUpdate, setScoreUpdate] = useState({
    frame_number: 1, player1_points: "", player2_points: "",
    player1_break: "", player2_break: "", frame_winner_id: ""
  });

  const activeTournaments = tournaments.filter(t => t.status !== "completed");

  const fetchScheduledMatches = useCallback(async (tournamentId) => {
    if (!tournamentId) { setScheduledMatches([]); return; }
    setLoadingMatches(true);
    try {
      const res = await axios.get(`${API}/tournaments/${tournamentId}/matches`);
      // Only show scheduled (unplayed) matches
      const scheduled = (res.data || []).filter(m => m.status === "scheduled");
      setScheduledMatches(scheduled);
    } catch {
      setScheduledMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTournamentId) fetchScheduledMatches(selectedTournamentId);
  }, [selectedTournamentId, fetchScheduledMatches]);

  const handleGoLive = async (match) => {
    setGoingLiveId(match.id);
    try {
      const payload = {};
      if (youtubeUrl.trim()) payload.youtube_url = youtubeUrl.trim();
      await axios.post(`${API}/matches/${match.id}/start-live`, payload, { withCredentials: true });
      toast.success(`Match is now LIVE: ${match.player1_name} vs ${match.player2_name}`);
      setYoutubeUrl("");
      fetchScheduledMatches(selectedTournamentId);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start live");
    } finally {
      setGoingLiveId(null);
    }
  };

  const handleUpdateScore = async () => {
    if (!selectedMatch) return;
    try {
      const data = {
        match_id: selectedMatch.id, frame_number: parseInt(scoreUpdate.frame_number),
        player1_points: scoreUpdate.player1_points ? parseInt(scoreUpdate.player1_points) : null,
        player2_points: scoreUpdate.player2_points ? parseInt(scoreUpdate.player2_points) : null,
        player1_break: scoreUpdate.player1_break ? parseInt(scoreUpdate.player1_break) : null,
        player2_break: scoreUpdate.player2_break ? parseInt(scoreUpdate.player2_break) : null,
        frame_winner_id: (scoreUpdate.frame_winner_id && scoreUpdate.frame_winner_id !== "none") ? scoreUpdate.frame_winner_id : null
      };
      const response = await axios.post(`${API}/matches/${selectedMatch.id}/update-score`, data, { withCredentials: true });
      toast.success(`Score updated! ${response.data.player1_frames}-${response.data.player2_frames}`);
      if (response.data.is_complete) { toast.success("Match completed!"); setSelectedMatch(null); }
      fetchData();
    } catch { toast.error("Failed to update score"); }
  };

  const handleEndMatch = async (winnerId) => {
    if (!selectedMatch) return;
    try {
      await axios.post(`${API}/matches/${selectedMatch.id}/end-live`, { winner_id: winnerId }, { withCredentials: true });
      toast.success("Match ended!");
      setSelectedMatch(null);
      fetchData();
    } catch { toast.error("Failed to end match"); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch { return dateStr; }
  };

  return (
    <div className="space-y-6">
      {/* Scheduled Matches — pick and go live */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Play className="w-5 h-5 text-primary" weight="fill" />
            Go Live — Select Match
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tournament selector */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Select Tournament</Label>
            <Select value={selectedTournamentId} onValueChange={setSelectedTournamentId}>
              <SelectTrigger className="bg-background border-border" data-testid="live-tournament-select">
                <SelectValue placeholder="Choose a tournament..." />
              </SelectTrigger>
              <SelectContent>
                {activeTournaments.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}{t.year ? ` (${t.year})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* YouTube URL */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <YoutubeLogo className="w-3.5 h-3.5 text-destructive" weight="fill" />
              YouTube Live Link (optional)
            </Label>
            <Input
              value={youtubeUrl}
              onChange={e => setYoutubeUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=... or embed URL"
              className="bg-background border-border"
              data-testid="youtube-url-input"
            />
          </div>

          {/* Scheduled matches list */}
          {selectedTournamentId && (
            <div>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">
                Unplayed Fixtures ({scheduledMatches.length})
              </p>
              {loadingMatches ? (
                <div className="py-6 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary mx-auto" />
                </div>
              ) : scheduledMatches.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No unplayed matches in this tournament</p>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-y-auto">
                  {scheduledMatches.map(m => (
                    <div
                      key={m.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/10 hover:border-primary/30 transition-colors"
                      data-testid={`scheduled-match-${m.id}`}
                    >
                      {/* Date & Time */}
                      <div className="flex flex-col items-center w-20 flex-shrink-0 text-center">
                        {m.scheduled_date && (
                          <span className="text-[10px] text-muted-foreground">{formatDate(m.scheduled_date)}</span>
                        )}
                        <span className="text-sm font-bold text-foreground">{m.scheduled_time || "TBD"}</span>
                        {m.table_name && (
                          <span className="text-[10px] text-muted-foreground">{m.table_name}</span>
                        )}
                      </div>

                      {/* Divider */}
                      <div className="w-px h-10 bg-border flex-shrink-0" />

                      {/* Players */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{m.player1_name}</p>
                        <p className="text-xs text-muted-foreground">vs</p>
                        <p className="text-sm font-medium truncate">{m.player2_name}</p>
                      </div>

                      {/* Meta */}
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <Badge variant="outline" className="text-[10px] capitalize border-border">
                          {m.round?.replace(/_/g, ' ')}
                        </Badge>
                        {m.best_of && <span className="text-[10px] text-muted-foreground">Bo{m.best_of}</span>}
                      </div>

                      {/* Go Live button */}
                      <Button
                        size="sm"
                        onClick={() => handleGoLive(m)}
                        disabled={goingLiveId === m.id}
                        className="bg-secondary text-secondary-foreground hover:bg-secondary/90 flex-shrink-0"
                        data-testid={`go-live-btn-${m.id}`}
                      >
                        <Lightning className="w-4 h-4 mr-1" weight="fill" />
                        {goingLiveId === m.id ? "Starting..." : "Go Live"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Scoring Panel */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lightning className="w-5 h-5 text-secondary" weight="fill" />
            Live Scoring
            {matches.length > 0 && (
              <Badge className="bg-secondary/20 text-secondary border-secondary/30 text-xs ml-2">
                {matches.length} live
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {matches.length > 0 ? (
            <div className="space-y-4">
              <Select value={selectedMatch?.id || ""} onValueChange={v => setSelectedMatch(matches.find(m => m.id === v))}>
                <SelectTrigger className="bg-background border-border" data-testid="live-match-select">
                  <SelectValue placeholder="Select live match" />
                </SelectTrigger>
                <SelectContent>
                  {matches.map(m => {
                    const p1 = players.find(p => p.id === m.player1_id);
                    const p2 = players.find(p => p.id === m.player2_id);
                    return <SelectItem key={m.id} value={m.id}>{p1?.name} vs {p2?.name} ({m.player1_score}-{m.player2_score})</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              {selectedMatch && (
                <>
                  <div className="p-4 rounded-lg bg-muted/30 text-center">
                    <div className="flex items-center justify-center gap-4">
                      <span className="text-sm font-medium">{players.find(p => p.id === selectedMatch.player1_id)?.name}</span>
                      <span className="text-3xl font-heading font-bold">{selectedMatch.player1_score} - {selectedMatch.player2_score}</span>
                      <span className="text-sm font-medium">{players.find(p => p.id === selectedMatch.player2_id)?.name}</span>
                    </div>
                    {selectedMatch.youtube_url && (
                      <a href={selectedMatch.youtube_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-destructive mt-2 hover:underline">
                        <YoutubeLogo className="w-3.5 h-3.5" weight="fill" />Watch Live
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input type="number" placeholder="Frame #" value={scoreUpdate.frame_number} onChange={e => setScoreUpdate({ ...scoreUpdate, frame_number: e.target.value })} className="bg-background border-border" data-testid="frame-number-input" />
                    <Input type="number" placeholder="P1 Points" value={scoreUpdate.player1_points} onChange={e => setScoreUpdate({ ...scoreUpdate, player1_points: e.target.value })} className="bg-background border-border" />
                    <Input type="number" placeholder="P2 Points" value={scoreUpdate.player2_points} onChange={e => setScoreUpdate({ ...scoreUpdate, player2_points: e.target.value })} className="bg-background border-border" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" placeholder="P1 Break" value={scoreUpdate.player1_break} onChange={e => setScoreUpdate({ ...scoreUpdate, player1_break: e.target.value })} className="bg-background border-border" />
                    <Input type="number" placeholder="P2 Break" value={scoreUpdate.player2_break} onChange={e => setScoreUpdate({ ...scoreUpdate, player2_break: e.target.value })} className="bg-background border-border" />
                  </div>
                  <Select value={scoreUpdate.frame_winner_id} onValueChange={v => setScoreUpdate({ ...scoreUpdate, frame_winner_id: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="frame-winner-select"><SelectValue placeholder="Frame winner" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Continue frame</SelectItem>
                      <SelectItem value={selectedMatch.player1_id}>{players.find(p => p.id === selectedMatch.player1_id)?.name}</SelectItem>
                      <SelectItem value={selectedMatch.player2_id}>{players.find(p => p.id === selectedMatch.player2_id)?.name}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleUpdateScore} className="w-full" data-testid="update-score-btn">
                    <FloppyDisk className="w-4 h-4 mr-2" />Update Score
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" className="border-destructive text-destructive" onClick={() => handleEndMatch(selectedMatch.player1_id)} data-testid="p1-wins-btn">
                      <Stop className="w-4 h-4 mr-1" />{players.find(p => p.id === selectedMatch.player1_id)?.name?.split(' ')[0]} Wins
                    </Button>
                    <Button variant="outline" className="border-destructive text-destructive" onClick={() => handleEndMatch(selectedMatch.player2_id)} data-testid="p2-wins-btn">
                      <Stop className="w-4 h-4 mr-1" />{players.find(p => p.id === selectedMatch.player2_id)?.name?.split(' ')[0]} Wins
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">No live matches — select a fixture above and tap "Go Live"</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
